<script setup lang="ts">
import type { YaPaySdk, YaPaySession } from '@/js/types/payment.ts';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  merchantId: string
  sandbox: boolean
  amount: number
}>();

const emit = defineEmits<{
  availability: [available: boolean]
}>();

const SDK_URL = 'https://pay.yandex.ru/sdk/v1/pay.js';
// Сплит не даёт API проверки доступности: единственный признак — виджет,
// который сам скрывается, если покупателю Сплит недоступен.
const RENDER_TIMEOUT = 5000;

const container = ref<HTMLElement | null>(null);
let session: YaPaySession | null = null;
let observer: ResizeObserver | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let isSettled = false;

function loadSdk(): Promise<YaPaySdk> {
  if (window.YaPay) {
    return Promise.resolve(window.YaPay);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.onload = () => window.YaPay ? resolve(window.YaPay) : reject(new Error('Yandex Pay SDK is not initialized'));
    script.onerror = () => reject(new Error('Yandex Pay SDK is not loaded'));
    document.head.appendChild(script);
  });
}

function formatAmount(amount: number) {
  return amount.toFixed(2);
}

function report(available: boolean) {
  isSettled = true;
  emit('availability', available);
}

function observeRender(element: HTMLElement) {
  observer = new ResizeObserver(() => {
    const isRendered = element.offsetHeight > 0;
    if (isRendered || isSettled) {
      report(isRendered);
    }
  });
  observer.observe(element);
  timer = setTimeout(() => {
    if (!isSettled) {
      report(element.offsetHeight > 0);
    }
  }, RENDER_TIMEOUT);
}

onMounted(async () => {
  const element = container.value;
  if (!element) {
    return;
  }
  try {
    const YaPay = await loadSdk();
    session = await YaPay.createSession({
      env: props.sandbox ? YaPay.PaymentEnv.Sandbox : YaPay.PaymentEnv.Production,
      version: 4,
      currencyCode: YaPay.CurrencyCode.Rub,
      merchantId: props.merchantId,
      totalAmount: formatAmount(props.amount),
      availablePaymentMethods: ['SPLIT'],
    }, {
      // Кнопку Пэй не монтируем — заказ создаётся формой чекаута,
      // оплата идёт редиректом на paymentUrl.
      onPayButtonClick: () => Promise.reject(new Error('Yandex Pay button is not used')),
      onFormOpenError: () => {},
    });
    session.mountWidget(element, {
      widgetType: YaPay.WidgetType.Ultimate,
      hasCheckoutButton: false,
    });
    observeRender(element);
  }
  catch {
    // SDK недоступен — доступность не проверить, оставляем способ оплаты как есть.
    report(true);
  }
});

watch(() => props.amount, (amount) => {
  session?.update(() => Promise.resolve({ totalAmount: formatAmount(amount) }));
});

onBeforeUnmount(() => {
  observer?.disconnect();
  if (timer) {
    clearTimeout(timer);
  }
});
</script>

<template>
  <div ref="container" />
</template>
